/**
 * The Today desktop header's sunrise/sunset location — a device-local
 * display preference, same category as `ownerName.ts`/`theme.ts` (NOT
 * domain event-log data; never synced, never appended to the event log).
 * Mirrors `ownerName.ts`'s exact shape: a small localStorage-backed
 * function pair, `null`-default when unset (never a fabricated fallback).
 *
 * Stores the city's NAME plus its resolved lat/long/timezone together (not
 * just the name string) so `computeSunTimes` can consume them directly, with
 * no re-lookup against `CITIES` required at read time — the picker writes
 * all of it in one call so they can never drift apart (e.g. `CITIES` being
 * edited in a later release, or an entry ever being renamed). `timezone` is
 * required (not just lat/long) because `computeSunTimes` needs the CITY's
 * own real UTC offset, not the device's — a stored value from before this
 * field existed fails validation below and is treated as unconfigured
 * (never a guessed/fabricated timezone).
 *
 * `getStoredLocation()` returns `null` when nothing is configured yet —
 * this is the real "unconfigured" state the sunrise/sunset pair keys off
 * of (design/screens/today-desktop-v2.md §4.1: "If no lat/long is
 * configured yet, this pair does not render" — quiet omission, never a
 * fabricated San Francisco default).
 */

export interface StoredLocation {
  readonly name: string;
  readonly lat: number;
  readonly long: number;
  readonly timezone: string;
}

const STORAGE_KEY = 'compass:location';

export const LOCATION_CHANGE_EVENT = 'compass:location-change';

export function getStoredLocation(): StoredLocation | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage?.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredLocation).name === 'string' &&
      typeof (parsed as StoredLocation).lat === 'number' &&
      typeof (parsed as StoredLocation).long === 'number' &&
      typeof (parsed as StoredLocation).timezone === 'string'
    ) {
      return parsed as StoredLocation;
    }
    return null;
  } catch {
    return null;
  }
}

export function setLocation(location: StoredLocation | null): void {
  if (typeof window === 'undefined') return;
  if (location === null) {
    window.localStorage?.removeItem(STORAGE_KEY);
  } else {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(location));
  }
  window.dispatchEvent(new CustomEvent(LOCATION_CHANGE_EVENT, { detail: location }));
}
